<script setup lang="ts">
import { computed } from 'vue'
import { VStats } from '@/modules/shared/components'
import type { Stat } from '@/modules/shared/components/types'
import type { Money } from '@/modules/shared/utils/money'
import { toNumber } from '@/modules/shared/utils/money'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'

const props = withDefaults(
  defineProps<{
    skeleton: boolean
    roi: number
    moic: number
    xirr?: number | null
    realizedGain: Money
    unrealizedGain: Money
    showKpi?: boolean
  }>(),
  { xirr: null, showKpi: true },
)

const { hidden } = useHiddenMetrics()

const hasMeaningfulMetrics = computed(
  () =>
    props.roi !== 0 ||
    props.moic !== 0 ||
    (props.xirr != null && props.xirr !== 0) ||
    toNumber(props.realizedGain) !== 0 ||
    toNumber(props.unrealizedGain) !== 0,
)

const kpiStats = computed<Stat[]>(() => {
  if (!hasMeaningfulMetrics.value) {
    return [
      { label: 'ROI', sublabel: 'return on investment', value: '\u2014', colspan: 1, visible: !hidden.value.roi },
      { label: 'XIRR', sublabel: 'extended IRR', value: '\u2014', colspan: 1 },
      { label: 'Realized G/L', sublabel: 'distributions received', value: '\u2014', colspan: 1 },
      { label: 'Unrealized G/L', sublabel: 'unrealized value change', value: '\u2014', colspan: 1 },
    ]
  }

  return [
    {
      label: 'ROI',
      sublabel: 'return on investment',
      value: props.roi,
      type: 'percent',
      colspan: 1,
      visible: !hidden.value.roi,
    },
    { label: 'XIRR', sublabel: 'extended IRR', value: props.xirr ?? 0, type: 'percent', colspan: 1 },
    {
      label: 'Realized G/L',
      sublabel: 'distributions received',
      value: props.realizedGain,
      type: 'currency',
      colspan: 1,
    },
    {
      label: 'Unrealized G/L',
      sublabel: 'unrealized value change',
      value: props.unrealizedGain,
      type: 'currency',
      colspan: 1,
    },
  ]
})
</script>

<template>
  <div v-if="showKpi" class="mb-8">
    <VStats :skeleton="skeleton" :stats="kpiStats" :mobile-cols="2" />
  </div>

  <div class="flex flex-col gap-8 lg:flex-row">
    <div class="min-w-0 lg:w-1/2">
      <slot name="raise-summary" />
    </div>

    <div class="min-w-0 space-y-5 lg:w-1/2">
      <slot name="details" />
      <slot name="deal-card" />

      <slot name="task-list" />
    </div>
  </div>
</template>
